import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check } from "lucide-react";
import { m } from "framer-motion";
import { useInView } from "react-intersection-observer";

export const PricingSection = () => {
  const [isYearly, setIsYearly] = useState(false);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const plans = [
    {
      name: "Free",
      monthlyPrice: "$0",
      yearlyPrice: "$0",
      period: "forever",
      features: ["2 documents/month", "Basic AI analysis", "PDF export only", "Community support"],
      cta: "Start Free",
      variant: "outline" as const,
      highlight: false,
    },
    {
      name: "Pro",
      monthlyPrice: "$15",
      yearlyPrice: "$12",
      period: "/month",
      badge: "Most Popular",
      features: [
        "Unlimited documents",
        "Advanced AI analysis",
        "All export formats",
        "Priority support",
        "Figma plugin",
        "Custom templates",
      ],
      cta: "Start 14-Day Trial",
      variant: "gradient" as const,
      highlight: true,
    },
    {
      name: "Team",
      monthlyPrice: "$40",
      yearlyPrice: "$32",
      period: "/user/month",
      features: [
        "Everything in Pro",
        "Team collaboration",
        "Shared workspace",
        "Admin controls",
        "Custom integrations",
        "Dedicated support",
      ],
      cta: "Contact Sales",
      variant: "outline" as const,
      highlight: false,
    },
  ];

  return (
    <section className="section-padding" id="pricing" ref={ref}>
      <div className="container-custom">
        <m.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Choose Your <span className="gradient-text">Plan</span>
          </h2>
          <p className="text-xl text-muted-foreground mb-8">
            Start with 14 days free. No credit card required.
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex items-center gap-3 glass-card p-1 rounded-full">
            <button
              onClick={() => setIsYearly(false)}
              className={`px-6 py-2 rounded-full font-medium transition-all duration-500 ${
                !isYearly
                  ? "bg-primary text-white shadow-md"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setIsYearly(true)}
              className={`px-6 py-2 rounded-full font-medium transition-all duration-500 flex items-center gap-2 ${
                isYearly
                  ? "bg-primary text-white shadow-md"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Yearly
              <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">Save 20%</span>
            </button>
          </div>
        </m.div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {plans.map((plan, index) => (
            <m.div
              key={index}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
              transition={{ delay: index * 0.1, duration: 0.8 }}
              className={`relative glass-card p-8 hover-glow hover-lift ${
                plan.highlight ? "ring-2 ring-primary md:scale-105" : ""
              }`}
            >
              {plan.badge && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white shadow-md">
                  {plan.badge}
                </Badge>
              )}
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold mb-4">{plan.name}</h3>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-5xl font-bold gradient-text">
                    {isYearly ? plan.yearlyPrice : plan.monthlyPrice}
                  </span>
                  <span className="text-muted-foreground">{plan.period}</span>
                </div>
                {isYearly && plan.yearlyPrice !== plan.monthlyPrice && (
                  <p className="text-sm text-muted-foreground mt-2">
                    Billed ${parseInt(plan.yearlyPrice.replace("$", "")) * 12}/year
                  </p>
                )}
              </div>

              <ul className="space-y-4 mb-8">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="h-3 w-3 text-white" />
                    </div>
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button variant={plan.variant} size="lg" className="w-full">
                {plan.cta}
              </Button>
            </m.div>
          ))}
        </div>

        <m.p
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="text-center text-muted-foreground mt-12"
        >
          All plans include 14-day free trial • No credit card required • Cancel anytime
        </m.p>
      </div>
    </section>
  );
};
